import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import PremiumFeatureBadge from "@babackapp/ui/components/premium-feature-badge";
import { cn } from "@babackapp/ui/lib/utils";
import { CheckIcon, Link2Icon } from "@radix-ui/react-icons";
import { PropsWithChildren, useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useResetFormOnSubmitSuccess from "./lib/use-reset-form-on-submit";

type SettingsFormContainerProps = {
  navigation?: SettingsFormNavigationProps["sections"];
};

export default function SettingsFormContainer({
  children,
  navigation,
}: PropsWithChildren<SettingsFormContainerProps>) {
  return (
    <div className="w-full flex justify-center pt-16 px-4">
      <div className="w-full flex-1 max-w-[740px] flex flex-col gap-8">
        {children}
      </div>
      {navigation && <SettingsFormNavigation sections={navigation} />}
    </div>
  );
}

type SettingsFormHeaderProps = {
  title: string;
  description: string;
};

type SettingsFormNavigationProps = {
  sections: { title: string; id: string; premium?: boolean }[];
};

export function SettingsFormNavigation({
  sections,
}: SettingsFormNavigationProps) {
  const { hash, pathname } = useLocation();
  const navigate = useNavigate();

  const scrollTo = useCallback(
    (hash: string, shouldNavigate = false) => {
      if (!hash.replace("#", "").trim()) return;
      document
        .querySelector(hash)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });

      if (shouldNavigate) {
        navigate(`${pathname}${hash}`);
      }
    },
    [pathname, navigate],
  );

  useEffect(() => {
    scrollTo(hash);
  }, [hash, scrollTo]);

  return (
    <div className="relative pb-16 hidden xl:block">
      <div className="flex-col gap-4 sticky top-32 border-l border-l-border hidden lg:flex ml-16 pl-6">
        {sections.map(({ title, id, premium }) => (
          <button
            key={id}
            type="button"
            role="button"
            className="text-left justify-start text-sm font-medium text-muted-foreground items-center inline-flex gap-2"
            onClick={() => scrollTo(`#${id}`, true)}
          >
            {title} {premium && <PremiumFeatureBadge />}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SettingsFormHeader({
  title,
  description,
}: SettingsFormHeaderProps) {
  return (
    <div className="flex flex-col gap-2 w-full">
      <h3 className="text-lg leading-6 font-medium text-gray-900">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground max-w-[500px]">
        {description}
      </p>
    </div>
  );
}

type SettingsFormSectionHeaderProps = {
  title: string;
  id?: string;
};

export function SettingsFormSectionHeader({
  title,
  id,
}: SettingsFormSectionHeaderProps) {
  return (
    <Link
      to={id ? `#${id}` : "#"}
      id={id}
      className="text-sm font-medium text-muted-foreground cursor-pointer flex gap-1 items-center hover:underline"
    >
      <Link2Icon /> {title}
    </Link>
  );
}

export function SettingsFormPageButtonBar() {
  const {
    formState: { dirtyFields },
  } = useFormContext();

  const t = useTranslation();

  useResetFormOnSubmitSuccess();

  /**
   * Weird behaviour:
   * https://github.com/react-hook-form/react-hook-form/issues/3213
   */
  const isDirty = !!Object.keys(dirtyFields).length;
  const isDirtyHeight = isDirty ? "h-14" : "h-0";

  return (
    <div
      className={`flex justify-center items-end sticky bottom-8 ${isDirtyHeight} transition-height duration-300 ease-in-out`}
    >
      <div
        className={cn(
          "pointer-events-auto w-1/2 transition-opacity transition-transform duration-500 ease-in-out",
          isDirty
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-[100px]",
        )}
      >
        <Button className="w-full items-center gap-2 flex">
          <CheckIcon className="size-4" />
          {t("misc.save")}
        </Button>
      </div>
    </div>
  );
}
